import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { AccountManagementService } from 'src/app/services/account-management.service';
import {
  LocationAccessKey,
  LocationAccessOption,
  LocationAccessService,
  LocationAccessUser,
  LocationAccessValue,
  LocationFilter,
  LocationFilterEvent,
  LocationSelectionChangeEvent,
  LocationSourceOption
} from 'src/app/services/location-access.service';
import { cloneFilterState } from 'src/app/services/data-filter.service';

@Component({
    selector: 'app-location-select',
    templateUrl: './location-select.component.html',
    styleUrls: ['./location-select.component.css'],
    standalone: false
})
export class LocationSelectComponent implements OnInit, OnChanges {
  private accountService = inject(AccountManagementService);
  private locationAccessService = inject(LocationAccessService);

  @Input() footprint!: string;
  @Input() user?: LocationAccessUser;
  @Input() isComparisonTool = false;
  @Input() isComparisonData = false;
  @Input() filter: LocationFilter = {
    region: ['All'],
    campus: ['All'],
    facility: ['All'],
    locationGroup: ['All']
  };
  @Input() deId = false;
  @Input() showReset = true;
  @Output() locationSelected = new EventEmitter<LocationFilterEvent>();
  @Output() initComplete = new EventEmitter<LocationFilterEvent>();
  @Output() clearTableSearch = new EventEmitter<void>();
  @Output() clearFilterPills = new EventEmitter<void>();

  regionOptions: LocationAccessOption[] = [];
  campusOptions: LocationAccessOption[] = [];
  facilityOptions: LocationAccessOption[] = [];
  locationGroupOptions: LocationAccessOption[] = [];

  private initialized = false;
  private firstInitDone = false;
  private viewInitialized = false;
  private loadSequence = 0;

  ngOnInit(): void {
    this.viewInitialized = true;
    this.filter = cloneFilterState(this.filter);
    this.filter = this.locationAccessService.sanitizeSelectedFilters(this.filter, this.user);
    this.loadLocationData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.viewInitialized) return;

    if (changes['filter'] && !changes['filter'].firstChange) {
      this.filter = this.locationAccessService.sanitizeSelectedFilters(
        cloneFilterState(changes['filter'].currentValue),
        this.user
      );
    }

    if (!changes['user'] || changes['user'].firstChange) return;

    const previousAccess = this.locationAccessSignature(changes['user'].previousValue);
    const currentAccess = this.locationAccessSignature(changes['user'].currentValue);

    if (previousAccess !== currentAccess) {
      this.initialized = false;
      this.filter = this.locationAccessService.sanitizeSelectedFilters(this.filter, this.user);
      this.loadLocationData();
    }
  }

  isDeId(name: string): string {
    return this.deId ? 'xxxxxx xxxxxx' : name;
  }

  loadLocationData(): void {
    const loadId = ++this.loadSequence;
    this.initializeOptions();
    this.loadRegions(loadId);
  }

  onRegionChanged(event: LocationSelectionChangeEvent): void {
    this.applyAllOption(event, 'region');

    if (this.locationAccessService.hasAll(this.filter.region)) {
      this.filter.campus = ['All'];
      this.filter.facility = ['All'];
      this.filter.locationGroup = ['All'];
    }

    this.sanitizeCurrentFilter();
    this.loadCampuses(++this.loadSequence);
    this.emitLocationChange();
  }

  onCampuseChanged(event: LocationSelectionChangeEvent): void {
    this.applyAllOption(event, 'campus');

    if (this.locationAccessService.hasAll(this.filter.campus)) {
      this.filter.facility = ['All'];
      this.filter.locationGroup = ['All'];
    }

    this.sanitizeCurrentFilter();
    this.loadFacilities(++this.loadSequence);
    this.emitLocationChange();
  }

  onFacilitieChanged(event: LocationSelectionChangeEvent): void {
    this.applyAllOption(event, 'facility');

    if (this.locationAccessService.hasAll(this.filter.facility)) {
      this.filter.locationGroup = ['All'];
    }

    this.sanitizeCurrentFilter();
    this.loadLocationGroups(++this.loadSequence);
    this.emitLocationChange();
  }

  onlocationGroupChanged(event: LocationSelectionChangeEvent): void {
    this.applyAllOption(event, 'locationGroup');
    this.emitLocationChange();
  }

  onClickReset(): void {
    this.initialized = false;
    this.filter = this.locationAccessService.buildInitialFilters(this.user, {
      footprint: this.footprint,
      changeValue: true
    });

    this.clearTableSearch.emit();
    this.clearFilterPills.emit();
    this.loadLocationData();
  }

  emitLocationChange(): void {
    this.sanitizeCurrentFilter();
    this.locationSelected.emit({
      region: this.filter.region,
      campus: this.filter.campus,
      facility: this.filter.facility,
      locationGroup: this.filter.locationGroup,
      changeValue: true
    });
  }

  shouldShowReset(): boolean {
    return this.showReset && (
      this.regionOptions?.length > 1 ||
      this.campusOptions?.length > 1 ||
      this.facilityOptions?.length > 1 ||
      this.locationGroupOptions?.length > 1
    );
  }

  shouldShowLocationControl(key: LocationAccessKey, options: LocationAccessOption[]): boolean {
    return options?.length > 1 ||
      (options?.length === 1 && this.locationAccessService.isRestrictedFor(this.user, key));
  }

  private loadRegions(loadId: number): void {
    if (this.locationAccessService.isRestrictedFor(this.user, 'region')) {
      this.regionOptions = this.locationAccessService.getAuthorizedOptions(this.user, 'region');
      this.filter.region = this.locationAccessService.sanitizeSelectedOptionValues(
        this.filter.region,
        this.regionOptions,
        this.user,
        'region'
      );
      this.loadCampuses(loadId);
      return;
    }

    this.accountService.getRegions().subscribe({
      next: (data) => {
        if (!this.isActiveLoad(loadId)) {
          return;
        }

        this.regionOptions = [
          { label: 'All Regions', value: 'All' },
          ...data.map((region: LocationSourceOption) => ({
            label: region.label ?? String(region.value ?? 'All'),
            value: String(region.value ?? 'All')
          }))
        ];
        this.applyDefaultSelection('region', this.regionOptions);
        this.sanitizeCurrentFilter();
        this.loadCampuses(loadId);
      },
      error: () => {
        if (this.isActiveLoad(loadId)) {
          this.completeInitialization();
        }
      }
    });
  }

  private loadCampuses(loadId: number): void {
    if (this.locationAccessService.isRestrictedFor(this.user, 'campus')) {
      this.campusOptions = this.locationAccessService.getAuthorizedOptions(this.user, 'campus');
      this.filter.campus = this.locationAccessService.sanitizeSelectedOptionValues(
        this.filter.campus,
        this.campusOptions,
        this.user,
        'campus'
      );
      this.loadFacilities(loadId);
      return;
    }

    const regionFilter = this.locationAccessService.hasAll(this.filter.region) ? ['All'] : this.filter.region;
    this.accountService.getCampusesFromRegionId(regionFilter).subscribe({
      next: (data) => {
        if (!this.isActiveLoad(loadId)) {
          return;
        }

        this.campusOptions = [
          { label: 'All Campuses', value: 'All' },
          ...data.map((campus: LocationSourceOption) => ({
            label: campus.label ?? String(campus.value ?? campus._id),
            value: String(campus.value ?? campus._id ?? 'All')
          }))
        ];
        this.applyDefaultSelection('campus', this.campusOptions);
        this.sanitizeCurrentFilter();
        this.loadFacilities(loadId);
      },
      error: () => {
        if (this.isActiveLoad(loadId)) {
          this.completeInitialization();
        }
      }
    });
  }

  private loadFacilities(loadId: number): void {
    const campusFilter = this.locationAccessService.hasAll(this.filter.campus) ? ['All'] : this.filter.campus;
    this.accountService.getFacilitiesFromCampusId(campusFilter, this.filter.region).subscribe({
      next: (data) => {
        if (!this.isActiveLoad(loadId)) {
          return;
        }

      const facilityOptions: LocationAccessOption[] = data.map((facility: LocationSourceOption) => ({
        label: facility.label ?? String(facility.value ?? facility._id),
        value: String(facility.value ?? facility._id ?? 'All')
      }));

      if (this.locationAccessService.isRestrictedFor(this.user, 'facility')) {
        this.facilityOptions = this.locationAccessService.filterAuthorizedOptions(
          this.user,
          'facility',
          facilityOptions,
          this.deId
        );
        this.filter.facility = this.locationAccessService.sanitizeSelectedOptionValues(
          this.filter.facility,
          this.facilityOptions,
          this.user,
          'facility'
        );
      } else {
        this.facilityOptions = [
          { label: 'All Sites', value: 'All' },
          ...facilityOptions.map((facility: LocationAccessOption) => ({
            ...facility,
            label: this.isDeId(facility.label)
          }))
        ];
        this.applyDefaultSelection('facility', this.facilityOptions);
        this.sanitizeCurrentFilter();
      }

        this.loadLocationGroups(loadId);
      },
      error: () => {
        if (this.isActiveLoad(loadId)) {
          this.completeInitialization();
        }
      }
    });
  }

  private loadLocationGroups(loadId: number): void {
    if (this.locationAccessService.isRestrictedFor(this.user, 'locationGroup')) {
      this.locationGroupOptions = this.sortOptions(
        this.locationAccessService.getAuthorizedOptions(this.user, 'locationGroup')
      );
      this.filter.locationGroup = this.locationAccessService.sanitizeSelectedOptionValues(
        this.filter.locationGroup,
        this.locationGroupOptions,
        this.user,
        'locationGroup'
      );
      this.completeInitialization();
      return;
    }

    const regionIds = this.selectedLabels(this.filter.region, this.regionOptions);
    const campusIds = this.selectedLabels(this.filter.campus, this.campusOptions);
    const facilityIds = this.selectedLabels(this.filter.facility, this.facilityOptions);

    this.accountService.getLocationGroupsByFacility(regionIds, campusIds, facilityIds).subscribe({
      next: (data) => {
        if (!this.isActiveLoad(loadId)) {
          return;
        }

        const groups = this.sortOptions(data
          .map((group: LocationSourceOption | string): LocationAccessOption | null => {
            const candidates = typeof group === 'string'
              ? [group]
              : [group?.name, group?.group, group?.label, group?.value];
            const label = candidates.find((value) =>
              (typeof value === 'string' || typeof value === 'number') &&
              !!String(value).trim() && String(value).trim() !== '[object Object]'
            );
            return label === undefined ? null : { label: String(label).trim(), value: String(label).trim() };
          })
          .filter((group): group is LocationAccessOption => group !== null));

        this.locationGroupOptions = [
          { label: 'All Areas', value: 'All' },
          ...groups
        ];
        this.applyDefaultSelection('locationGroup', this.locationGroupOptions);
        this.sanitizeCurrentFilter();
        this.completeInitialization();
      },
      error: () => {
        if (this.isActiveLoad(loadId)) {
          this.completeInitialization();
        }
      }
    });
  }

  private completeInitialization(): void {
    if (this.initialized) {
      return;
    }

    this.sanitizeCurrentFilter();
    this.initialized = true;

    if (!this.firstInitDone) {
      this.firstInitDone = true;
      this.initComplete.emit({
        region: this.filter.region,
        campus: this.filter.campus,
        facility: this.filter.facility,
        locationGroup: this.filter.locationGroup,
        changeValue: false
      });
    } else {
      this.emitLocationChange();
    }
  }

  private applyAllOption(event: LocationSelectionChangeEvent, filterKey: LocationAccessKey): void {
    const eventValue = event?.value || [];
    const hasAll = eventValue.includes('All');
    const selectedSpecificValue = eventValue.length > 1 && eventValue[eventValue.length - 1] !== 'All';

    if (selectedSpecificValue) {
      this.filter[filterKey] = eventValue.filter((val: LocationAccessValue) => val !== 'All');
    } else if (hasAll) {
      this.filter[filterKey] = ['All'];
    } else if (!eventValue.length) {
      this.filter[filterKey] = ['All'];
    } else {
      this.filter[filterKey] = eventValue.filter((val: LocationAccessValue) => val !== 'All');
    }

    this.sanitizeCurrentFilter();
  }

  private sanitizeCurrentFilter(): void {
    this.filter = this.locationAccessService.sanitizeSelectedFilters(this.filter, this.user);
    this.sanitizeCurrentFilterForOptions('region', this.regionOptions);
    this.sanitizeCurrentFilterForOptions('campus', this.campusOptions);
    this.sanitizeCurrentFilterForOptions('facility', this.facilityOptions);
    this.sanitizeCurrentFilterForOptions('locationGroup', this.locationGroupOptions);
  }

  private sanitizeCurrentFilterForOptions(key: LocationAccessKey, options: LocationAccessOption[]): void {
    if (!options?.length || !this.locationAccessService.isRestrictedFor(this.user, key)) {
      return;
    }

    this.filter[key] = this.locationAccessService.sanitizeSelectedOptionValues(
      this.filter[key],
      options,
      this.user,
      key
    );
  }

  private applyDefaultSelection(key: LocationAccessKey, options: LocationAccessOption[]): void {
    // A caller-provided selection takes precedence over the user's default.
    if (this.filter[key]?.length && !this.locationAccessService.hasAll(this.filter[key])) {
      return;
    }
    const defaultKeys: Record<LocationAccessKey, keyof LocationAccessUser> = {
      region: 'defaultRegions',
      campus: 'defaultCampuses',
      facility: 'defaultFacilities',
      locationGroup: 'defaultLocationGroups'
    };
    const defaultValues = this.locationAccessService.normalizeValues(this.user?.[defaultKeys[key]], key);

    if (!defaultValues.length || defaultValues.includes('All')) {
      return;
    }

    this.filter[key] = defaultValues.map((value) => {
      const option = options.find((item) => item.value === value || item.label === value);
      return option?.value || value;
    });
  }

  private selectedLabels(selectedValues: LocationAccessValue[], options: LocationAccessOption[]): string[] {
    if (!selectedValues?.length || this.locationAccessService.hasAll(selectedValues)) {
      return ['All'];
    }

    return selectedValues.map((value) => {
      const option = options.find((item) => item.value === value);
      return option?.label || value;
    });
  }

  private sortOptions(options: LocationAccessOption[]): LocationAccessOption[] {
    const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });
    return options.sort((a, b) => collator.compare(a.label, b.label));
  }

  private initializeOptions(): void {
    this.regionOptions = this.regionOptions?.length > 1 ? this.regionOptions : [];
    this.campusOptions = this.campusOptions?.length > 1 ? this.campusOptions : [];
    this.facilityOptions = this.facilityOptions?.length > 1 ? this.facilityOptions : [];
    this.locationGroupOptions = this.locationGroupOptions?.length > 1 ? this.locationGroupOptions : [];
  }

  private isActiveLoad(loadId: number): boolean {
    return loadId === this.loadSequence;
  }

  private locationAccessSignature(user?: LocationAccessUser): string {
    return JSON.stringify(this.locationAccessService.normalizeUserLocations(user));
  }
}
